import { Badge, Box, Center, Flex, HStack, Image, Text, VStack } from "@chakra-ui/react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import React from "react";
import type { Messages } from "../../core/api";
import { auth } from "../../core/auth";
import { env } from "../../env";
import useApi from "../../shared/hooks/useApi";
import { CaregiverId } from "../../shared/schema/schema";
import EntityProfileModalFrame from "../entityProfileModal/components/EntityProfileModalFrame";
import { entityProfileModalColors } from "../entityProfileModal/entityProfileModal.theme";
import useEntityProfileSectionNavigation from "../entityProfileModal/hooks/useEntityProfileSectionNavigation";
import CaregiverProfileHeader from "./components/CaregiverProfileHeader";
import CaregiverProfileNavigation from "./components/CaregiverProfileNavigation";
import CaregiverProfileSections from "./components/CaregiverProfileSections";
import { useCaregiverProfileAiCapabilities } from "./caregiverProfileModal.ai";
import { caregiverProfileBridgeActions } from "./caregiverProfileModal.actions";
import {
  caregiverProfileSections,
  CaregiverProfileSection,
  CaregiverProfileSubsection,
} from "./caregiverProfileModal.sections";
import { CaregiverProfileCaregiver } from "./caregiverProfileModal.types";

type Props = {
  caregiverId: CaregiverId;
  showBillingFeature: boolean;
  onClose: () => void;
  onReady?: () => void;
};

function canViewSubsection(
  subsection: CaregiverProfileSubsection,
  showBillingFeature: boolean,
  caregiver: CaregiverProfileCaregiver | undefined
) {
  if (
    caregiver !== undefined &&
    subsection.hiddenForCaregiverStatuses?.includes(caregiver.status) === true
  ) {
    return false;
  }

  if (subsection.isBillingFeature === true && showBillingFeature === false) {
    return false;
  }

  if (subsection.isOnboardingOnly === true) {
    const authState = auth.getAuthState();
    const agencyId = authState.type === "authenticated" ? authState.data.agency.id : null;

    if (agencyId !== env.MEDFLYT_AGENCY_ID) {
      return false;
    }
  }

  if (subsection.permissionKey === undefined) {
    return true;
  }

  return auth.isAllowedTo(subsection.permissionKey as Messages["AgencyMemberPermission"]);
}

function getVisibleSections(
  sections: CaregiverProfileSection[],
  showBillingFeature: boolean,
  caregiver: CaregiverProfileCaregiver | undefined
) {
  return sections
    .map((section) => ({
      ...section,
      subsections: section.subsections.filter((subsection) =>
        canViewSubsection(subsection, showBillingFeature, caregiver)
      ),
    }))
    .filter((section) => section.subsections.length > 0);
}

function CaregiverProfileLoadingHeader(props: { caregiverId: CaregiverId }) {
  return (
    <Flex alignItems="flex-start" gap={5} justifyContent="space-between" px={8} py={4}>
      <Flex gap={4} minW={0}>
        <Image
          alt=""
          border="3px solid"
          borderColor="gray.300"
          borderRadius="full"
          boxSize="70px"
          flexShrink={0}
          objectFit="cover"
          src="admin/images/avatars/avatar_placeholder.png"
        />
        <VStack alignItems="flex-start" gap={1} minW={0}>
          <HStack alignItems="center" flexWrap="wrap" spacing={2}>
            <Text
              color={entityProfileModalColors.hoverTitle}
              fontSize="25px"
              fontWeight={600}
              lineHeight="1.1"
            >
              Caregiver #{props.caregiverId}
            </Text>
            <Badge borderRadius="2px" colorScheme="blue" fontSize="11px" px={2} py={1}>
              Loading
            </Badge>
          </HStack>
          <Text color={entityProfileModalColors.muted} fontSize="14px" lineHeight="1.25">
            Loading caregiver profile details...
          </Text>
        </VStack>
      </Flex>
    </Flex>
  );
}

function CaregiverProfileContent(props: {
  caregiver?: CaregiverProfileCaregiver;
  caregiverId: CaregiverId;
  showBillingFeature: boolean;
}) {
  const caregiver = props.caregiver;
  const visibleSections = React.useMemo(
    () => getVisibleSections(caregiverProfileSections, props.showBillingFeature, caregiver),
    [caregiver, props.showBillingFeature]
  );
  const sectionNavigation = useEntityProfileSectionNavigation({
    entity: "caregiver",
    sectionIdPrefix: "react-caregiver-section",
    sections: visibleSections,
  });

  // Same capabilities, provided by whichever caregiver window is on screen.
  useCaregiverProfileAiCapabilities(caregiver, sectionNavigation, visibleSections);
  const activeSection = sectionNavigation.activeSection;

  if (activeSection === undefined) {
    return (
      <Center flex={1}>
        <Text color={entityProfileModalColors.muted}>No caregiver profile sections available.</Text>
      </Center>
    );
  }

  return (
    <Flex bg={entityProfileModalColors.surface} direction="column" h="100%" minH={0}>
      <Box bg={entityProfileModalColors.surface}>
        {caregiver === undefined ? (
          <CaregiverProfileLoadingHeader caregiverId={props.caregiverId} />
        ) : (
          <CaregiverProfileHeader actions={caregiverProfileBridgeActions} caregiver={caregiver} />
        )}
        <CaregiverProfileNavigation
          activeSectionIndex={sectionNavigation.activeSectionIndex}
          activeSubsectionIndex={sectionNavigation.activeSubsectionIndex}
          sections={visibleSections}
          onSectionChange={sectionNavigation.handleSectionChange}
          onSubsectionChange={sectionNavigation.handleSubsectionChange}
        />
      </Box>

      <Box
        ref={sectionNavigation.contentRef}
        bg={entityProfileModalColors.surface}
        flex={1}
        minH={0}
        overflowY="auto"
        pb="56px"
        scrollPaddingBottom="56px"
        onScroll={sectionNavigation.handleContentScroll}
      >
        <CaregiverProfileSections
          key={props.caregiverId}
          actions={caregiverProfileBridgeActions}
          caregiver={caregiver}
          caregiverId={props.caregiverId}
          isProfileLoading={caregiver === undefined}
          section={activeSection}
        />
      </Box>
    </Flex>
  );
}

export default function CaregiverProfileModal(props: Props) {
  const { queries } = useApi();
  const { onReady } = props;
  const didNotifyReadyRef = React.useRef(false);
  const caregiverQuery = useQuery({
    ...queries.caregiver.get(props.caregiverId),
    placeholderData: keepPreviousData,
  });

  React.useEffect(() => {
    if (
      didNotifyReadyRef.current === false &&
      (caregiverQuery.data !== undefined || caregiverQuery.isError)
    ) {
      didNotifyReadyRef.current = true;
      onReady?.();
    }
  }, [caregiverQuery.data, caregiverQuery.isError, onReady]);

  return (
    <EntityProfileModalFrame
      entityLabel="caregiver"
      isError={caregiverQuery.isError}
      isLoading={caregiverQuery.isLoading || caregiverQuery.data === undefined}
      onClose={props.onClose}
      onRetry={() => void caregiverQuery.refetch()}
    >
      <CaregiverProfileContent
        caregiver={caregiverQuery.data}
        caregiverId={props.caregiverId}
        showBillingFeature={props.showBillingFeature === true}
      />
    </EntityProfileModalFrame>
  );
}
